import type { Locale } from '@/lib/i18n/config'

import type {
    AgreementStatus,
    ComplianceStatus,
    Criticality,
    CsfFunction,
    FindingStatus,
    InitiativeStatus,
    Rag,
    RiskStatus,
    SheetKey
} from '@/lib/data/types'

import { useI18n } from '@/lib/i18n/context'
import { defineText, plural } from '@/lib/i18n/text'

/* ------------------------------------------------------------------ */
/* Labels for normalized values                                        */
/* ------------------------------------------------------------------ */

export interface Labels {
    initiativeStatus: Record<InitiativeStatus, string>
    complianceStatus: Record<ComplianceStatus, string>
    findingStatus: Record<FindingStatus, string>
    riskStatus: Record<RiskStatus, string>
    agreementStatus: Record<AgreementStatus, string>
    criticality: Record<Criticality, string>
    rag: Record<Rag, string>
    ragMeaning: Record<Rag, string>
    csf: Record<CsfFunction, string>
    sheet: Record<SheetKey, string>
    field: Record<string, string>
}

const ES: Labels = {
    initiativeStatus: { active: 'En curso', closed: 'Cerrada', backlog: 'Backlog', onHold: 'En pausa', cancelled: 'Cancelada' },
    complianceStatus: { compliant: 'Cumplido', inProgress: 'En proceso', pending: 'Pendiente', nonCompliant: 'No cumple', notApplicable: 'No aplica' },
    findingStatus: { open: 'Abierto', inRemediation: 'En remediación', closed: 'Cerrado', accepted: 'Riesgo aceptado' },
    riskStatus: { open: 'Abierto', treating: 'En tratamiento', mitigated: 'Mitigado', accepted: 'Aceptado', closed: 'Cerrado' },
    agreementStatus: { done: 'Cumplido', inProgress: 'En proceso', pending: 'Pendiente', cancelled: 'Cancelado' },
    criticality: { critical: 'Crítica', high: 'Alta', medium: 'Media', low: 'Baja' },
    rag: { green: 'Verde', amber: 'Ámbar', red: 'Rojo', none: 'Sin semáforo' },
    ragMeaning: { green: 'En plan', amber: 'Requiere atención', red: 'Fuera de plan', none: 'Sin evaluar' },
    csf: { GV: 'Gobernar', ID: 'Identificar', PR: 'Proteger', DE: 'Detectar', RS: 'Responder', RC: 'Recuperar' },
    sheet: {
        portfolio: 'Portafolio vigente',
        history: 'Portafolio histórico',
        compliance: 'Cumplimiento seguimiento',
        csf: 'Heatmap CSF',
        design: 'Diseño y arquitectura',
        findings: 'Diseño hallazgos',
        volumetry: 'Volumetrías',
        tiers: 'Tiers',
        risks: 'Perfil de riesgos',
        budget: 'Presupuesto',
        agreements: 'Acuerdos de coordinación',
        basics: 'Básicos de seguridad',
        params: 'Parámetros'
    },
    field: {
        id: 'ID',
        name: 'Nombre',
        program: 'Programa',
        division: 'División',
        bu: 'Unidad de negocio',
        owner: 'Responsable',
        status: 'Estatus',
        progress: '% de avance',
        planned: '% planeado',
        start: 'Fecha de inicio',
        end: 'Fecha de fin',
        criticality: 'Criticidad',
        domain: 'Dominio',
        tower: 'Torre',
        health: 'Semáforo',
        budget: 'Presupuesto',
        notes: 'Comentarios',
        cut: 'Fecha de corte',
        requirement: 'Requisito',
        framework: 'Marco normativo',
        due: 'Fecha compromiso',
        closed: 'Fecha de cierre',
        evidence: 'Evidencia',
        fn: 'Función',
        category: 'Categoría',
        categoryName: 'Nombre de categoría',
        current: 'Madurez actual',
        target: 'Meta u objetivo',
        period: 'Periodo',
        activity: 'Actividad',
        project: 'Proyecto',
        type: 'Tipo',
        hoursPlanned: 'Horas estimadas',
        hoursActual: 'Horas reales',
        title: 'Hallazgo',
        detected: 'Fecha de detección',
        asset: 'Activo',
        exercise: 'Ejercicio',
        indicator: 'Indicador',
        value: 'Valor',
        unit: 'Unidad',
        direction: 'Sentido',
        tier: 'Tier',
        rto: 'RTO',
        rpo: 'RPO',
        dataClass: 'Clasificación de datos',
        risk: 'Riesgo',
        probability: 'Probabilidad',
        impact: 'Impacto',
        score: 'Puntaje',
        treatment: 'Tratamiento',
        review: 'Fecha de revisión',
        trend: 'Tendencia',
        lineId: 'ID de línea',
        line: 'Línea presupuestal',
        plan: 'Plan',
        actual: 'Ejecutado',
        committed: 'Comprometido',
        currency: 'Moneda',
        vendor: 'Proveedor',
        rate: 'Tipo de cambio',
        agreement: 'Acuerdo',
        forum: 'Foro',
        date: 'Fecha',
        control: 'Control',
        key: 'Parámetro'
    }
}

const EN: Labels = {
    initiativeStatus: { active: 'In progress', closed: 'Closed', backlog: 'Backlog', onHold: 'On hold', cancelled: 'Cancelled' },
    complianceStatus: { compliant: 'Compliant', inProgress: 'In progress', pending: 'Pending', nonCompliant: 'Non-compliant', notApplicable: 'Not applicable' },
    findingStatus: { open: 'Open', inRemediation: 'In remediation', closed: 'Closed', accepted: 'Risk accepted' },
    riskStatus: { open: 'Open', treating: 'In treatment', mitigated: 'Mitigated', accepted: 'Accepted', closed: 'Closed' },
    agreementStatus: { done: 'Done', inProgress: 'In progress', pending: 'Pending', cancelled: 'Cancelled' },
    criticality: { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' },
    rag: { green: 'Green', amber: 'Amber', red: 'Red', none: 'No status' },
    ragMeaning: { green: 'On track', amber: 'Needs attention', red: 'Off track', none: 'Not assessed' },
    csf: { GV: 'Govern', ID: 'Identify', PR: 'Protect', DE: 'Detect', RS: 'Respond', RC: 'Recover' },
    sheet: {
        portfolio: 'Current portfolio',
        history: 'Portfolio history',
        compliance: 'Compliance tracking',
        csf: 'CSF heatmap',
        design: 'Design and architecture',
        findings: 'Design findings',
        volumetry: 'Volumetrics',
        tiers: 'Tiers',
        risks: 'Risk profile',
        budget: 'Budget',
        agreements: 'Coordination agreements',
        basics: 'Security basics',
        params: 'Parameters'
    },
    field: {
        id: 'ID',
        name: 'Name',
        program: 'Program',
        division: 'Division',
        bu: 'Business unit',
        owner: 'Owner',
        status: 'Status',
        progress: '% progress',
        planned: '% planned',
        start: 'Start date',
        end: 'End date',
        criticality: 'Criticality',
        domain: 'Domain',
        tower: 'Tower',
        health: 'RAG status',
        budget: 'Budget',
        notes: 'Comments',
        cut: 'Cut-off date',
        requirement: 'Requirement',
        framework: 'Framework',
        due: 'Due date',
        closed: 'Closing date',
        evidence: 'Evidence',
        fn: 'Function',
        category: 'Category',
        categoryName: 'Category name',
        current: 'Current maturity',
        target: 'Target',
        period: 'Period',
        activity: 'Activity',
        project: 'Project',
        type: 'Type',
        hoursPlanned: 'Estimated hours',
        hoursActual: 'Actual hours',
        title: 'Finding',
        detected: 'Detection date',
        asset: 'Asset',
        exercise: 'Exercise',
        indicator: 'Indicator',
        value: 'Value',
        unit: 'Unit',
        direction: 'Direction',
        tier: 'Tier',
        rto: 'RTO',
        rpo: 'RPO',
        dataClass: 'Data classification',
        risk: 'Risk',
        probability: 'Likelihood',
        impact: 'Impact',
        score: 'Score',
        treatment: 'Treatment',
        review: 'Review date',
        trend: 'Trend',
        lineId: 'Line ID',
        line: 'Budget line',
        plan: 'Plan',
        actual: 'Actual',
        committed: 'Committed',
        currency: 'Currency',
        vendor: 'Vendor',
        rate: 'Exchange rate',
        agreement: 'Agreement',
        forum: 'Forum',
        date: 'Date',
        control: 'Control',
        key: 'Parameter'
    }
}

const LABELS: Record<Locale, Labels> = { 'es-mx': ES, en: EN }

export const labelsFor = (locale: Locale): Labels => LABELS[locale]

export const useLabels = (): Labels => LABELS[useI18n().locale]

/* ------------------------------------------------------------------ */
/* Shared interface copy                                               */
/* ------------------------------------------------------------------ */

export const COMMON = defineText({
    all: 'Todas',
    allPeriods: 'Todos los periodos',
    search: 'Buscar',
    searchPlaceholder: 'Buscar…',
    noResults: 'Sin coincidencias',
    noData: 'Sin datos para mostrar',
    noDataFiltered: 'No hay registros con los filtros actuales.',
    notInFile: 'Esta vista no tiene información en la base cargada.',
    vsPrevCut: 'vs corte anterior',
    vsPrevPeriod: 'vs periodo anterior',
    noPrevious: 'Sin corte anterior',
    expandAll: 'Expandir todo',
    collapseAll: 'Contraer todo',
    print: 'Imprimir',
    close: 'Cerrar',
    cancel: 'Cancelar',
    save: 'Guardar',
    edit: 'Editar',
    showMore: 'Ver más',
    showLess: 'Ver menos',
    total: 'Total',
    average: 'Promedio',
    others: 'Otros',
    unassigned: 'Sin asignar',
    yes: 'Sí',
    no: 'No',
    of: 'de',
    rows: (count: number) => `${count} ${plural(count, 'registro', 'registros')}`,
    showing: (shown: number, total: number) => `Mostrando ${shown} de ${total}`,
    sortBy: (column: string) => `Ordenar por ${column}`,
    toggleSection: 'Mostrar u ocultar sección',
    viewTable: 'Ver tabla',
    viewChart: 'Ver gráfica',
    legend: 'Leyenda',
    ragLegendTitle: 'Significado del semáforo',
    cutLine: 'Corte',
    target: 'Meta',
    plan: 'Plan',
    actual: 'Real',
    days: 'días',
    overdue: 'Vencida',
    overdueShort: 'Vencido',
    dueSoon: 'Por vencer',
    onTime: 'En tiempo',
    noDate: 'Sin fecha',
    company: '[Nombre de Empresa]',
    directorate: 'Dirección de Ciberseguridad',
    filtersApplied: 'Filtros aplicados',
    noFilters: 'Sin filtros',
    referenceDate: 'Fecha de corte',
    generatedAt: 'Generado',
    source: 'Fuente',
    page: 'Página',
    menu: 'Abrir menú',
    skip: 'Saltar al contenido'
}, {
    all: 'All',
    allPeriods: 'All periods',
    search: 'Search',
    searchPlaceholder: 'Search…',
    noResults: 'No matches',
    noData: 'No data to show',
    noDataFiltered: 'No records match the current filters.',
    notInFile: 'The loaded database has no information for this view.',
    vsPrevCut: 'vs previous cut-off',
    vsPrevPeriod: 'vs previous period',
    noPrevious: 'No previous cut-off',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    print: 'Print',
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    showMore: 'Show more',
    showLess: 'Show less',
    total: 'Total',
    average: 'Average',
    others: 'Others',
    unassigned: 'Unassigned',
    yes: 'Yes',
    no: 'No',
    of: 'of',
    rows: (count: number) => `${count} ${plural(count, 'record', 'records')}`,
    showing: (shown: number, total: number) => `Showing ${shown} of ${total}`,
    sortBy: (column: string) => `Sort by ${column}`,
    toggleSection: 'Show or hide section',
    viewTable: 'View table',
    viewChart: 'View chart',
    legend: 'Legend',
    ragLegendTitle: 'What the RAG status means',
    cutLine: 'Cut-off',
    target: 'Target',
    plan: 'Plan',
    actual: 'Actual',
    days: 'days',
    overdue: 'Overdue',
    overdueShort: 'Overdue',
    dueSoon: 'Due soon',
    onTime: 'On time',
    noDate: 'No date',
    company: '[Nombre de Empresa]',
    directorate: 'Cybersecurity Office',
    filtersApplied: 'Applied filters',
    noFilters: 'No filters',
    referenceDate: 'Cut-off date',
    generatedAt: 'Generated',
    source: 'Source',
    page: 'Page',
    menu: 'Open menu',
    skip: 'Skip to content'
})
